import React from 'react';
import { UpdateProvider, type AppUpdater } from '@hopdrive/sdk-app-updater/react';
import { MuiUpdateToast } from '@hopdrive/sdk-app-updater/mui';
import config from '@config';
import { DEALER_BUNDLE, updaterDevAids } from './dealerUpdaterConfig';
import { UpdateRequiredBridge } from './updateRequired';

interface DealerUpdaterProps {
  children?: React.ReactNode;
  /** A test seam: an updater the test drives by hand instead of one that polls. */
  updater?: AppUpdater;
  /** A test seam: what reloading the page does. */
  reload?: () => void;
}

/**
 * Picks up new deploys in an open tab and reloads it once the user is idle and
 * no screen holds unsaved work. Mount once, inside the MUI theme: the toast and
 * the save-or-discard dialog render in it.
 *
 * `handleChunkLoadErrors` hands Vite's `vite:preloadError` to the updater: a
 * missing lazy chunk gets a few spaced-out reloads, then the toast's "Reload to
 * try again" alert, instead of Vite reloading the page in a loop.
 */
const DealerUpdater: React.FC<DealerUpdaterProps> = ({ children, updater, reload }) => (
  <UpdateProvider
    bundle={DEALER_BUNDLE}
    updater={updater}
    reload={reload}
    handleChunkLoadErrors
    {...updaterDevAids(config.appEnv)}
  >
    {children}
    <UpdateRequiredBridge />
    <MuiUpdateToast />
  </UpdateProvider>
);

export default DealerUpdater;
